/**
 * English copy for the research lab. Authoritative: `el.ts` is typed against this
 * file's exact shape, so a missing Greek key is a compile error rather than a
 * silent English fallback in production.
 *
 * ── Copy guardrails (same discipline as the funnel's en.ts) ──
 *
 *  - No personality types, no scores, no compatibility ratings, no "your type is".
 *    Nothing here may describe a result about the respondent, because the survey
 *    produces none.
 *  - No clinical or diagnostic framing. The loneliness and responsiveness items
 *    are self-report measures for research, not an assessment, and must never be
 *    worded as one.
 *  - No invented statistics and no social proof. Every figure under `interstitials`
 *    is published research by someone else and carries its citation in `sources`;
 *    nothing in this app reports findings from this survey, because it has none.
 *  - "May help", never "proven to". The same restraint the product practises
 *    everywhere else.
 *  - Consent copy is legally reviewed text. Do not rewrite the consent block for
 *    tone — see docs/research-lab.md.
 *
 * ── Placeholder marker ──
 *
 * A `__PENDING__`-prefixed string forces INSTRUMENT_READY to be false (see
 * instrument.test.ts) — none remain below as of docs/research-lab-survey-draft.md
 * landing on 2026-07-30.
 */

export const en = {
	brand: 'Bonded Research Lab',
	language: 'Language',
	changeLanguage: 'Change language',
	back: 'Back',
	continue: 'Continue',
	skip: 'Skip',
	loading: 'Loading…',
	retry: 'Try again',
	stepOf: 'Question {{n}} of {{total}}',
	copy: 'Copy',
	copied: 'Copied',

	notOpen: {
		eyebrow: 'Not open yet',
		title: 'This survey is not open',
		body: 'The questions are still being finalised. Nothing has been collected, and there is nothing to fill in yet.',
	},

	/**
	 * The marketing landing page at `/`.
	 *
	 * Deliberately a separate namespace from `landing` below, which is the
	 * survey's own first screen. The two are different pages with different jobs
	 * and must be editable without disturbing each other.
	 *
	 * Order is on purpose: the research first, because this is the research
	 * domain and it is what the headline asks; the app second, as the reason the
	 * question is being asked at all.
	 */
	home: {
		eyebrow: 'Bonded Research',
		title: 'What makes a conversation go deeper?',
		ledeResearch:
			'A short survey on how connection actually works. Five minutes, no account, and no result about you — that is on purpose.',
		ledeApp:
			'We ask because we build Bonded, a two-person question game for couples, dates and friends, and we would rather know what helps than guess.',
		ctaSurvey: 'Start the survey',
		// Opens bonded.studio/app, not the App Store: the app is iPhone-only and
		// live in a few countries first, so a store link dead-ends for everyone else.
		ctaApp: 'Get the app',
		phoneAltHome: 'The Bonded app home screen on iPhone, showing your avatar and a session ready to start',
		phoneAltProfile: 'The Bonded app profile screen on iPhone, showing a built avatar, a short bio and answered conversation prompts',
		// Standalone footer labels, not the ones under `landing`. Those read as the
		// object of "you agree to our …", which is a different grammatical case in
		// Greek — see el.ts. Keeping a separate pair here stops the two uses from
		// being quietly merged later.
		terms: 'Terms of Service',
		privacy: 'Privacy Policy',
	},

	landing: {
		eyebrow: 'Bonded Research',
		title: 'What makes a conversation go deeper?',
		// {{questions}} is derived from the flow (plannedQuestionCount), not typed
		// here. Not named `count` — that is i18next's plural selector.
		// This line said "Twelve questions" for a while after the instrument had
		// stopped asking twelve, and a hand-typed count will drift again at the
		// next revision.
		lede: '{{questions}} questions about how connection actually works for you. Five minutes, no account, no right answers.',
		points: {
			why: {
				icon: '🔬',
				title: 'Why we are asking',
				body: 'We build an app for talking to people you care about. We would rather know what actually helps than guess.',
			},
			what: {
				icon: '📊',
				title: 'What happens to it',
				body: 'Your answers become part of a group. We intend to publish what we find.',
			},
			you: {
				icon: '🙈',
				title: 'What you get back',
				body: 'Nothing about you personally — no score, no type, no result. That is on purpose.',
			},
		},
		cta: 'Start',
		agreementPrefix: 'By continuing you agree to our',
		terms: 'Terms of Service',
		and: 'and',
		privacy: 'Privacy Policy',
	},

	consent: {
		eyebrow: 'Before you start',
		title: 'One decision first',
		// Reviewed consent text. Each line states one specific, verifiable fact
		// about what happens to the data — no reassurance that is not also a
		// technical guarantee elsewhere in this repo.
		points: {
			purpose: {
				icon: '🔬',
				body: 'Your answers are used for research into connection and conversation, and for findings we may publish.',
			},
			aggregate: {
				icon: '👥',
				body: 'Nothing published ever describes one person. Any figure representing fewer than five people is withheld automatically.',
			},
			noProfile: {
				icon: '🚫',
				body: 'You will not be scored, typed, rated, or matched. The survey produces no result about you.',
			},
			control: {
				icon: '✉️',
				body: 'Answers are kept while this research runs. You will get a reference code at the end — email it to us any time and we delete yours.',
			},
			// Accepting is what releases the held TikTok pixel (see lib/tiktok.ts
			// and NodeScreen's `decide` handler), so accepting has to be told that.
			// Consent that does not describe the processing it unlocks is not
			// informed consent — GDPR Arts. 4(11), 6(1)(a), 7(2). Kept last
			// because it is the one point that is about us rather than about them.
			advertising: {
				icon: '📊',
				body: 'Saying yes also lets TikTok’s measurement tag load, so we can tell which ads bring people here. Saying no keeps it switched off.',
			},
		},
		accept: 'Yes, use my answers',
		decline: 'No, not this time',
		declineNote: 'Declining ends the survey here, and costs you nothing.',
	},

	declined: {
		eyebrow: 'Understood',
		title: 'Thanks anyway',
		body: 'Nothing has been recorded beyond your decision not to take part, which we keep so we know how often people decline.',
		waitlistCta: 'Join the waitlist instead',
		done: 'Close',
	},

	sections: {
		about_you: {
			title: 'A little about you',
			body: 'Three quick questions, so findings can be broken down by group.',
		},
		connection_habits: {
			title: 'How you connect',
			body: 'How conversation tends to go for you at the moment.',
		},
		loneliness: {
			title: 'Feeling connected',
			body: 'Four quick questions about how connected you feel day to day.',
		},
		felt_responsiveness: {
			title: 'Being heard',
			body: 'Three questions about how it feels when you talk to someone close to you.',
		},
		self_disclosure: {
			title: 'Opening up',
			body: 'Three questions about how easy or hard it is to say what you actually think.',
		},
		closing: {
			title: 'Anything else',
			body: 'One optional question, in your own words.',
		},
	},

	/**
	 * The charts on the section-intro screens.
	 *
	 * Every number here is published research by other people, cited in `sources`
	 * below and rendered on the card itself. None of it comes from this survey —
	 * this survey has no findings yet, and showing running totals mid-run would
	 * tell respondents what everyone else answered.
	 */
	interstitials: {
		connection_habits: {
			title: 'People who report the highest wellbeing spend more of the day talking',
			caption: 'Share of waking hours spent in conversation, comparing the happiest and unhappiest people in the study.',
			labels: { happiest: 'Happiest', unhappiest: 'Unhappiest' },
		},
		loneliness: {
			title: 'Loneliness is highest among the young, not the old',
			caption: 'Share who say they feel very or fairly lonely. The gap runs the opposite way to the assumption most people hold.',
			labels: { age_19_29: 'Aged 19–29', everyone: 'Everyone', age_65_plus: 'Aged 65+' },
		},
		felt_responsiveness: {
			title: 'More Europeans now say they have nobody to ask for help',
			caption: 'Share of adults across the EU reporting no one they could turn to for help of any kind — moral, practical or financial.',
			labels: { '2018': '2018', '2022': '2022' },
		},
		self_disclosure: {
			title: 'The happiest people had twice as many substantive conversations',
			caption: 'Share of recorded conversations that went beyond small talk.',
			labels: { happiest: 'Happiest', unhappiest: 'Unhappiest' },
		},
	},

	/**
	 * Citations. Every chart names its source and its sample size — a figure from
	 * 79 undergraduates and a figure from 142 countries deserve different weight,
	 * and hiding that difference would be the dishonest part.
	 */
	sources: {
		mehl: 'Mehl, Vazire, Holleran & Clark (2010), Psychological Science 21(4). 79 people wore recorders for four days.',
		gallup: 'Meta-Gallup, Global State of Social Connections (2023). 142 countries, around 1,000 people in each.',
		eurostat: 'Eurostat, EU-SILC quality-of-life module (2018 and 2022).',
	},

	/**
	 * Question copy.
	 *
	 * `points` is the full set of anchors for a scale, one label per point, and it
	 * is what a reproduced instrument requires: the UCLA short form's middle
	 * anchor is "Some of the time", and rendering only the two ends left
	 * respondents choosing an unlabelled middle circle — which is a different
	 * question from the published one. `low`/`high` stay for the ends of a row
	 * where per-point labels would not fit.
	 */
	questions: {
		age_band: {
			title: 'Which age group are you in?',
			options: {
				'18_24': '18–24',
				'25_34': '25–34',
				'35_44': '35–44',
				'45_54': '45–54',
				'55_64': '55–64',
				'65_plus': '65 or older',
				prefer_not_to_say: 'Prefer not to say',
			},
		},
		relationship_status: {
			title: 'Which best describes your current relationship situation?',
			options: {
				single: 'Single',
				dating: 'Dating',
				partnered: 'In a relationship',
				married: 'Married or in a civil partnership',
				separated: 'Separated or divorced',
				widowed: 'Widowed',
				self_describe: 'I describe it another way',
				prefer_not_to_say: 'Prefer not to say',
			},
		},
		living_situation: {
			title: 'Who do you currently live with?',
			help: 'Select all that apply.',
			options: {
				alone: 'I live alone',
				partner: 'A partner',
				children: 'Children',
				family: 'Parents or other family',
				friends: 'Friends or housemates',
				other: 'Another arrangement',
				prefer_not_to_say: 'Prefer not to say',
			},
		},
		confidant_count: {
			title: 'How many people can you talk to about something personal or important?',
			options: {
				no_one: 'No one',
				one: '1 person',
				two_three: '2–3 people',
				four_six: '4–6 people',
				seven_plus: '7 or more',
				prefer_not_to_say: 'Prefer not to say',
			},
		},
		conversation_blockers: {
			title: 'What usually makes deeper conversations difficult for you?',
			help: 'Choose as many as apply, or none.',
			options: {
				dont_know_what_to_ask: "I don't know how to start",
				feels_awkward: 'It feels awkward',
				fear_of_reaction: "I'm afraid of the response",
				hard_to_find_moment: "It's hard to find the right moment",
				stay_on_small_talk: 'We stay on small talk',
				no_right_person: "I don't have the right person",
				nothing_stops_me: 'Nothing gets in the way',
				something_else: 'Something else',
			},
		},
		// Asked only when two or more blockers were chosen, and offered only the
		// ones that were — see the instrument's showWhen/optionsFrom. The labels
		// match conversation_blockers exactly: the same barrier reworded between
		// the two screens would read as a different option.
		blocker_primary: {
			title: 'Which of those gets in the way most?',
			help: 'Choose the one that fits best.',
			options: {
				dont_know_what_to_ask: "I don't know how to start",
				feels_awkward: 'It feels awkward',
				fear_of_reaction: "I'm afraid of the response",
				hard_to_find_moment: "It's hard to find the right moment",
				stay_on_small_talk: 'We stay on small talk',
				no_right_person: "I don't have the right person",
				nothing_stops_me: 'Nothing gets in the way',
				something_else: 'Something else',
			},
		},
		blocker_other_text: {
			title: 'What else gets in the way?',
			help: 'A few words is plenty, and you can skip this.',
			placeholder: 'Optional',
			hint: 'Please leave out names, email addresses, and phone numbers — including your own.',
		},
		conversation_helpers: {
			title: 'What most helps a conversation feel meaningful to you?',
			help: 'Choose the one that fits best.',
			options: {
				feeling_listened_to: 'Feeling listened to',
				honesty: 'Honesty',
				right_questions: 'Asking the right questions',
				enough_time: 'Having enough time',
				feeling_safe: 'Feeling safe and unjudged',
				sharing_personal: 'Sharing something personal',
				something_else: 'Something else',
			},
		},
		dating_app_sentiment: {
			title: 'How do dating apps fit into your life right now?',
			options: {
				never_used: "I've never used one",
				currently_using: 'Using one now',
				burnt_out: 'Burnt out on them',
				quit: "I've quit them",
				prefer_not_to_say: 'Prefer not to say',
			},
		},
		// The three UCLA short-form items, with their published anchors. Do not
		// reword these three or their points — they are reproduced, not written.
		loneliness_companionship: {
			title: 'How often do you feel that you lack companionship?',
			low: 'Hardly ever',
			high: 'Often',
			points: ['Hardly ever or never', 'Some of the time', 'Often'],
		},
		loneliness_left_out: {
			title: 'How often do you feel left out?',
			low: 'Hardly ever',
			high: 'Often',
			points: ['Hardly ever or never', 'Some of the time', 'Often'],
		},
		loneliness_isolated: {
			title: 'How often do you feel isolated from others?',
			low: 'Hardly ever',
			high: 'Often',
			points: ['Hardly ever or never', 'Some of the time', 'Often'],
		},
		// The ONS harmonised direct question, verbatim. These five labels are what
		// make this sample comparable against national statistics; paraphrasing any
		// of them quietly breaks that comparison while still looking fine.
		loneliness_direct: {
			title: 'How often do you feel lonely?',
			low: 'Never',
			high: 'Often or always',
			points: ['Never', 'Hardly ever', 'Occasionally', 'Some of the time', 'Often or always'],
		},
		responsiveness_understood: {
			title: 'When I share something personal with people close to me, I generally feel understood.',
			low: 'Strongly disagree',
			high: 'Strongly agree',
			points: ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'],
		},
		responsiveness_cared_for: {
			title: 'The people I am closest to notice when something is bothering me, even if I do not say it.',
			low: 'Strongly disagree',
			high: 'Strongly agree',
			points: ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'],
		},
		responsiveness_attention: {
			title: 'When I share something personal, people close to me give me their full attention.',
			low: 'Strongly disagree',
			high: 'Strongly agree',
			points: ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'],
		},
		disclosure_depth_ease: {
			title: 'I find it easy to talk about things that actually matter to me — fears, hopes, things I am unsure about — with new people.',
			low: 'Very hard',
			high: 'Very easy',
			points: ['Very hard', 'Fairly hard', 'Neither easy nor hard', 'Fairly easy', 'Very easy'],
		},
		disclosure_gap: {
			title: 'How big is the gap between what you can easily talk about and what you wish you could talk about?',
			options: {
				none: 'No gap',
				slight: 'A slight gap',
				noticeable: 'A noticeable gap',
				large: 'A large gap',
			},
		},
		depth_satisfaction: {
			title: 'How satisfied are you with the depth of the conversations in your life?',
			low: 'Very dissatisfied',
			high: 'Very satisfied',
			points: [
				'Very dissatisfied',
				'Dissatisfied',
				'Neither satisfied nor dissatisfied',
				'Satisfied',
				'Very satisfied',
			],
		},
	},

	scale: { of: '{{value}} of {{total}}' },

	quote: {
		eyebrow: 'Optional',
		title: 'Anything you would want someone to understand?',
		body: 'A sentence or two, if you feel like it. You can skip this — most people do, and that is fine.',
		label: 'In your own words',
		placeholder: 'Optional',
		hint: 'Please leave out names, email addresses, and phone numbers — including your own.',
		errors: {
			tooLong: 'That is a little long. Please shorten it.',
			contact: 'Please remove email addresses, phone numbers, and handles.',
			failed: 'That did not save. You can try again or skip.',
		},
		save: 'Continue',
		skip: 'Skip this',
	},

	quoteConsent: {
		eyebrow: 'One more choice',
		title: 'May we quote this?',
		body: 'If we publish findings, we might use what you wrote — never with your name, and never alongside anything that identifies you.',
		yours: 'What you wrote:',
		accept: 'Yes, you may quote it',
		decline: 'No, keep it private',
		declineNote: 'Your answer still counts toward the findings either way.',
		remove: 'Delete what I wrote',
	},

	behaviorLink: {
		eyebrow: 'Because you have an account',
		title: 'May we compare this with how you use Bonded?',
		body: 'Looking at your answers next to how sessions actually go would tell us whether the app does what people say they want. Entirely optional, and it changes nothing about your account.',
		note: 'This covers your own activity only, and never anything another person wrote.',
		accept: 'Yes, that is fine',
		decline: 'No, just my answers',
	},

	/**
	 * The email screen. Required, single Continue button, no opt-in checkbox —
	 * `notice` is the disclosure that replaces it, and it is not optional: an
	 * undisclosed opt-in is the part that would fail a GDPR review.
	 *
	 * The heading, body and CTA talk about the findings and nothing else. This is
	 * the end of a research survey, not a place to sell the app — someone who has
	 * just answered seventeen questions about loneliness is owed a plain question,
	 * not a pitch.
	 *
	 * `notice` is the one line that mentions the product, and it stays. The
	 * address really does go into the shared waitlist_signups table
	 * (routes/research.ts passes source: 'research_lab'), so removing the line
	 * while keeping the join would be exactly the undisclosed opt-in the checkbox
	 * was dropped in favour of disclosing. If the waitlist join ever goes away,
	 * this line goes with it.
	 */
	email: {
		eyebrow: 'Last step',
		title: 'Where should we send the findings?',
		body: 'Leave an address and we will email you what this research turns up. That is the only reason to leave one.',
		emailLabel: 'Your email',
		placeholder: 'you@example.com',
		notice: 'You will also be told when Bonded opens. Unsubscribe any time.',
		onFileTitle: 'We will use the address on your account',
		onFileBody: 'Nothing to type — you are signed in, so we already have it.',
		submit: 'Continue',
		position: 'You are number {{position}} on the list.',
		joinedNoPosition: 'You are on the list.',
		alreadyOnList: 'You were already on the list.',
		errors: { email: 'Please check that address.', failed: 'That did not go through. Please try again.' },
	},

	thanks: {
		eyebrow: 'Done',
		title: 'Thank you',
		// Deliberately no summary, no score, no "your type". The survey produces
		// nothing about the respondent, so there is nothing to show back.
		body: 'Your answers are in. They will only ever appear as part of a group, never on their own.',
		codeLabel: 'Your reference code',
		codeHelp: 'We have emailed this to you as well. Keep it if you ever want to contact us about your answers.',
		dataRequest: 'Managing your data',
	},

	/**
	 * Deletion requests are handled by a human over email rather than a button.
	 * A one-tap delete at the end of a survey destroys the data the survey exists
	 * to collect, usually by accident; a request costs a respondent one email and
	 * is honoured just as completely.
	 */
	dataRequest: {
		eyebrow: 'Your data',
		title: 'Deleting your answers',
		body: 'Email {{email}} and include your reference code. We will delete your answers and confirm it. You do not need to give a reason, and there is nothing else to fill in.',
		codeLabel: 'Your reference code',
		noCode: 'If you no longer have your code, email us from the address you gave us and we will find it.',
		back: 'Back',
	},

	enter: {
		loading: 'Opening the survey…',
		expiredTitle: 'This link has been used',
		expiredBody: 'Survey links open once and then expire. Open the survey again from the Bonded app to get a fresh one.',
	},

	errors: {
		title: 'Something went wrong',
		body: 'That is on us. Your answers so far are saved.',
		offline: 'You appear to be offline.',
	},
} as const

export type Translation = typeof en
